import { AppTooltip, Badge, Button, Text, Card as UiCard } from '@components'
import type { Setup } from '@here/api'
import { useService } from 'solid-services'
import { useNavigate } from '@solidjs/router'
import dayjs from 'dayjs'
import { ArrowRight, ChevronDown, ChevronUp, Star } from 'lucide-solid'
import { type Component, Match, Show, Switch } from 'solid-js'
import { css } from 'styled-system/css'
import { Box, Circle, Flex, Grid } from 'styled-system/jsx'
import { useCreate } from '../../Create/state'
import Visualisation from '../../Visualisation' // move to appropriate spot
import { useFilters } from '../state/filter'
import { Author } from './Author'
import { CommanderScore } from './CommanderScore'
import Details from './Details'
import DetailsOverlaid from './DetailsOverlaid'
import { Images } from './Images'
import { MessageCount } from './MessageCount'
import { CommunityScore } from './CommunityScore'

interface ItemProps {
	setup?: Setup & { count?: number }
	layout: 'noramlised' | 'image'
	hideEditButton?: boolean
}

const Card: Component<ItemProps> = (props) => {
	const navigate = useNavigate()
	const { fetchFilters } = useService(useFilters)()
	const { editSetup } = useService(useCreate)()

	const isLong = props.setup?.position === 'long'

	const isNew = () => dayjs(props.setup?.created_at).isAfter(dayjs().subtract(3, 'day'))

	return (
		<Show when={props.setup}>
			<UiCard.Root
				class={css({
					display: 'flex',
					transition: 'all 0.2s',
					_hover: {
						boxShadow: '1px 1px 5px 0px rgba(0, 0, 0, 0.2)',
					},
          borderWidth: '2px !important',
					width: '100%',
					position: 'relative',
					flexDirection: 'column',
					borderColor: isNew() ? 'tscGreen.10' : '',
				})}
			>
				<Box
					p={2}
					position="absolute"
					top="0"
					right="0"
					w="100%"
					zIndex="1"
				>
					<Flex
						px={2}
						pt={2}
						alignItems="start"
						justifyContent="space-between"
					>
						<Flex
							gap={1}
							alignItems="center"
						>
							<Badge
								bg="neutral"
								color="white"
								variant="solid"
							>
								{props.setup?.symbol}
							</Badge>
							<Show
								when={props.setup?.position === 'long'}
								fallback={
									<Circle
										size={5}
										background="var(--colors-error)"
										color="bg.default"
									>
										<ChevronDown  />
									</Circle>
								}
							>
								<Circle
									size={5}
									background="tscGreen.10"
									color="bg.default"
								>
									<ChevronUp />
								</Circle>
							</Show>
						</Flex>
						<Grid
							gap={1}
							justifyItems="end"
						>
							<MessageCount channelId={props.setup?.channel_id ?? undefined} />
							<Show when={isNew()}>
								<Badge
									size="sm"
									variant="solid"
									justifyContent="center"
									colorPalette="tscGreen"
									class={css({
										color: 'tscGreen.light.2',
										background: 'tscGreen.default',
									})}
								>
									New
								</Badge>
							</Show>
							<CommanderScore channelId={props.setup?.id ?? undefined} />
							<CommunityScore
								channelId={props.setup?.id ?? undefined}
								setup={props.setup}
							/>
						</Grid>
					</Flex>
				</Box>

				<UiCard.Body
					p={2}
					pb={1}
				>
					<Grid gap={1}>
						<Switch>
							<Match when={props.layout === 'noramlised'}>
								<Visualisation
									height="300px"
									setup={props.setup!}
								/>
							</Match>
							<Match when={props.layout === 'image'}>
								<Images setup={props.setup} />
							</Match>
						</Switch>
						<DetailsOverlaid setup={props.setup} />
					</Grid>
				</UiCard.Body>

				<UiCard.Footer
					p={2}
					gap={2}
					display="grid"
					justifyContent="stretch"
					onClick={(e) => navigate(`/setups/${props.setup?.id}`)}
					cursor="pointer"
					background="gray.2"
				>
					<Grid gridTemplateColumns='1fr auto' alignItems="center">
            <Box minW={0}>
              <AppTooltip content={props.setup?.title} delay={1300}>
                <Text whiteSpace='nowrap' overflow='hidden' textOverflow='ellipsis' color="neutralSubtle" >{props.setup?.title}</Text>
              </AppTooltip>
            </Box>
						<Details setup={props.setup} />
					</Grid>
					<Flex justifyContent="space-between">
						<Author
							createdBy={props.setup?.created_by}
							created_at={props.setup?.created_at}
						/>

						<Flex
							gap={2}
							alignItems="center"
						>
							<Show when={!props.hideEditButton}>
								<Button
									size="xs"
									colorPalette="fg"
									borderColor="fg.subtle"
									color="fg.default"
									variant="outline"
									onClick={(e) => {
										e.stopPropagation()
										editSetup(props.setup as any)
									}}
								>
									Update
									<ArrowRight />
								</Button>
							</Show>
						</Flex>
					</Flex>
				</UiCard.Footer>
			</UiCard.Root>
		</Show>
	)
}

export default Card
